import React, {
  memo,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  cloneElement,
  forwardRef,
  ForwardedRef,
} from 'react';

import { FreeMode, Controller } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper/types';
import 'swiper/css';
import 'swiper/css/free-mode';

import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';

import * as S from './style';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// 폭이 잡히기 전엔 넘침 판정도 위치도 의미가 없다
const isMeasured = (swiper: SwiperClass): boolean =>
  !!swiper?.wrapperEl && swiper.wrapperEl.clientWidth > 0;

interface IProps {
  id?: string;
  items?: React.ReactNode;
  selectedValue?: number | string | undefined;
  speed?: number;
  freeMode?: boolean;
  simulateTouch?: boolean;
  allowTouchMove?: boolean;
  resistanceRatio?: number;
  className?: string;
  isDesktop?: boolean;
  keepFirstItemLeftAligned?: boolean;
  autoUpdate?: boolean;
  onSwiper?: (swiper: SwiperClass) => void;
}

// eslint-disable-next-line react/display-name
const MenuBarSwiper = forwardRef(
  (
    {
      id,
      className,
      items,
      freeMode = true,
      simulateTouch = true,
      allowTouchMove = true,
      resistanceRatio = 0.85,
      selectedValue,
      speed = 300,
      isDesktop,
      keepFirstItemLeftAligned = false,
      autoUpdate = false,
      onSwiper,
    }: IProps,
    ref: ForwardedRef<HTMLDivElement>
  ): React.JSX.Element => {
    const [$selectedValue, setSelectedValue] = useState<
      number | string | undefined
    >();
    const [$centeredSlides, setCenteredSlides] = useState(false); // 기본 왼쪽 정렬
    const [currentSwiper, setCurrentSwiper] = useState<SwiperClass>();

    // 선택 항목의 위치
    const getSelectedIndex = useCallback((): number => {
      if (!(items instanceof Array) || selectedValue === undefined) return -1;

      return items.findIndex(
        (v: { props: { value: number | string | undefined } }) =>
          String(v.props.value) === String(selectedValue)
      );
    }, [items, selectedValue]);

    /**
     * 넘칠 때만 센터 정렬. 선택 항목이 없거나 첫 항목 고정이면 왼쪽 정렬
     * el.clientWidth는 컨테이너 좌우 padding을 포함하므로 wrapper의 가시 폭으로 잰다
     */
    const getCenteredSlides = useCallback(
      (swiper: SwiperClass, idx: number): boolean => {
        if (idx === -1 || (keepFirstItemLeftAligned && idx === 0)) return false;

        return swiper.wrapperEl.clientWidth < swiper.wrapperEl.scrollWidth;
      },
      [keepFirstItemLeftAligned]
    );

    /**
     * centeredSlides에선 snapGrid 앞쪽이 0으로 뭉쳐 swiper의 activeIndex가 화면 중앙
     * 항목이 되고 선택 항목과 어긋난다. swiper 내부 onResize는 그 activeIndex로
     * slideTo하므로, 뒤이어 실행되는 이 핸들러에서 선택 항목 기준으로 되돌린다.
     */
    const realignToSelected = useCallback(
      (swiper: SwiperClass) => {
        const idx = getSelectedIndex();

        setCenteredSlides(getCenteredSlides(swiper, idx));
        if (idx !== -1) swiper.slideTo(idx, 0, false);
      },
      [getSelectedIndex, getCenteredSlides]
    );

    // EVENT HANDLER : onSwiperSlideTo
    const onSwiperSlideTo = useCallback(
      (swiper: SwiperClass) => {
        if (items instanceof Array && selectedValue !== undefined) {
          const idx = getSelectedIndex();

          // 실제값 선택시 센터정렬 여부
          if (swiper && swiper.params)
            setCenteredSlides(getCenteredSlides(swiper, idx));
          setTimeout(() => {
            if (swiper && idx !== -1 && swiper.params) {
              // swiper.slideTo(idx, speed, swiper.update());
              swiper.slideTo(idx, speed, false);

              if (isDesktop) {
                /**
                 * 활성화로 인해 슬라이드 내부 콘텐츠 폭이 변했을 수 있으므로 갱신
                 * 순서: 슬라이드 목록 → 컨테이너 사이즈 → 전체 업데이트
                 */
                swiper.updateSlides();
                swiper.updateSize();
                swiper.update();
              }
            }
          }, 100);
        }
      },
      [
        items,
        selectedValue,
        speed,
        isDesktop,
        getSelectedIndex,
        getCenteredSlides,
      ]
    );

    useEffect(() => {
      if (selectedValue !== $selectedValue) {
        setSelectedValue(selectedValue);
        if (currentSwiper) onSwiperSlideTo(currentSwiper);
      }
    }, [selectedValue, currentSwiper, onSwiperSlideTo, $selectedValue]);

    // 아이템 목록의 동일성 판정용 키(렌더마다 새로 만들어지는 배열이라 참조 비교 불가)
    const itemsKey = useMemo(
      () =>
        items instanceof Array
          ? items
              .map(
                (v: React.ReactElement<{ value?: number | string }> | null) =>
                  `${String(v?.key)}:${String(v?.props?.value)}`
              )
              .join('|')
          : '',
      [items]
    );

    const prevItemsKeyRef = useRef(itemsKey);

    /**
     * 목록이 통째로 바뀌면(1뎁스 전환 등) 이전 목록의 스크롤 위치와 관성이 그대로 남는다.
     * 관성이 살아 있으면 새 목록이 그 트랜지션에 끌려가 왼쪽으로 쓸려나가므로,
     * 페인트 전에 트랜지션을 끊고 선택 항목 위치로 즉시 맞춘다.
     */
    useIsomorphicLayoutEffect(() => {
      if (prevItemsKeyRef.current === itemsKey) return;
      prevItemsKeyRef.current = itemsKey;

      const swiper = currentSwiper;
      if (!swiper || swiper.destroyed) return;
      if (!(items instanceof Array) || selectedValue === undefined) return;

      /**
       * swiper.update()가 이미 목표 위치로 클램프해 두면 slideTo가 건너뛰어
       * 남은 트랜지션이 그대로 재생되므로, 트랜지션 제거는 slideTo와 별개로 먼저 한다
       * (setTransition은 public 타입에 없어 wrapper 스타일을 직접 지정)
       */
      swiper.wrapperEl.style.transitionDuration = '0ms';
      swiper.wrapperEl.style.transitionDelay = '0ms';
      swiper.animating = false;

      const idx = items.findIndex(
        (v: { props: { value: number | string | undefined } }) =>
          String(v.props.value) === String(selectedValue)
      );

      swiper.slideTo(idx === -1 ? 0 : idx, 0, false);
    }, [itemsKey, items, selectedValue, currentSwiper]);

    /**
     * [autoUpdate 전용] 폰트 지연 로드로 경계 길이 아이템의 스크롤 범위가 stale해져
     * 스와이프가 안 되는 문제 대응 > 첫 터치 시 update()로 1회 재측정(once, 자동 해제)
     */
    useEffect(() => {
      if (!autoUpdate || !currentSwiper) return undefined;

      const swiper = currentSwiper;
      const refresh = () => {
        if (!swiper.destroyed) swiper.update();
      };

      swiper.once('touchStart', refresh);

      return () => {
        if (!swiper.destroyed) swiper.off('touchStart', refresh);
      };
    }, [autoUpdate, currentSwiper]);

    const cName = classNameBind(['menubar-swiper', className || '']);

    // ITEM RENDERER : getListRender
    const getListRender = useCallback(
      (el: React.ReactNode) => {
        if (el instanceof Array) {
          return el?.map((v: React.ReactElement<any>) => {
            if (v) {
              return (
                <SwiperSlide
                  key={v.key}
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                  data-value={v.props.value ? v.props.value : null}
                  // onClick={() => {
                  //   if (v.props?.onClick) {
                  //     v.props.onClick();
                  //   }
                  // }}
                >
                  {cloneElement(v, { selectedValue }) as React.JSX.Element}
                </SwiperSlide>
              );
            }
            return null;
          });
        }
        return null;
      },
      [selectedValue]
    );

    return (
      <S.MenuBarSwiper ref={ref} className={cName} id={id}>
        <Swiper
          modules={[FreeMode, Controller]}
          spaceBetween={0}
          speed={speed}
          slidesPerView="auto"
          freeMode={freeMode}
          centeredSlides={$centeredSlides}
          centeredSlidesBounds
          simulateTouch={simulateTouch}
          allowTouchMove={allowTouchMove}
          resistanceRatio={resistanceRatio}
          touchEventsTarget="container"
          onInit={(swiper: SwiperClass) => {
            setCurrentSwiper(swiper);

            // 폭이 잡힌 뒤에 정렬한다. 아직이면 첫 resize에서 잡힌다
            if (isMeasured(swiper)) onSwiperSlideTo(swiper);
          }}
          onResize={(swiper: SwiperClass) => {
            // 펼친 상태에선 wrapper가 줄바꿈돼 scrollWidth가 컨테이너 폭까지 줄어든다
            if (swiper.el.closest('.unFolding')) return;
            if (!isMeasured(swiper)) return;

            realignToSelected(swiper);
          }}
          onSwiper={(swiper: SwiperClass) => onSwiper && onSwiper(swiper)}
        >
          {getListRender(items)}
        </Swiper>
      </S.MenuBarSwiper>
    );
  }
);

export default memo(MenuBarSwiper);
